import type { FC, ReactNode } from "react";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import type { AssistantDraft, AssistantDraftValueKind } from "../../api/assistant";
import { REGISTRATION_FORM_TYPE } from "../../api/assistant";
import {
  CircleXIcon,
  PartnerIcon3,
  PersonIcon3,
  RegistrationControllerIcon2,
  SystemIcon3,
  TenantIcon3,
  YesIcon,
} from "../../shared/icons";
import { uniqueKeys } from "../../shared/uniqueKeys";
import { AssistantProposalFrame } from "./AssistantProposalFrame";
import { useAssistantApi } from "./AssistantApiContext";

type DraftAnswer = AssistantDraft["answers"][number];

type DraftSection = { name: string; answers: Array<DraftAnswer> };

const ROW_GAP = "4px";

const ROW_GRID = { display: "grid", gridTemplateColumns: "35% minmax(0, 1fr)", columnGap: 4, minHeight: 32 } as const;

const GLYPHS: Partial<Record<AssistantDraftValueKind, FC<{ size: number }>>> = {
  person: PersonIcon3,
  unit: TenantIcon3,
  system: SystemIcon3,
  partner: PartnerIcon3,
};

const sectionsOf = (answers: Array<DraftAnswer>) =>
  answers.reduce<Array<DraftSection>>((sections, answer) => {
    const name = answer.section ?? "";
    const section = sections.find(candidate => candidate.name === name);
    if (section) section.answers.push(answer);
    else sections.push({ name, answers: [answer] });
    return sections;
  }, []);

type AssistantDraftCardProps = { drafts: Array<AssistantDraft>; isSubmitting: boolean; onSubmit: () => void };

export const AssistantDraftCard: FC<AssistantDraftCardProps> = ({ drafts, isSubmitting, onSubmit }) => {
  const draft = drafts.find(candidate => isRegistration(candidate)) ?? drafts[0];
  const sections = sectionsOf(draft.answers);

  return (
    <Stack sx={{ gap: 2 }}>
      <AssistantProposalFrame gap={1}>
        <DraftHeader draft={draft} />
        <DraftBody draft={draft} sections={sections} />
      </AssistantProposalFrame>
      <DraftClosing
        additions={addedNames(drafts.filter(candidate => !isRegistration(candidate)))}
        isSubmitting={isSubmitting}
        onSubmit={onSubmit}
        order={sections.length + 1}
      />
    </Stack>
  );
};

const isRegistration = (draft: AssistantDraft) => (draft.formType ?? REGISTRATION_FORM_TYPE) === REGISTRATION_FORM_TYPE;

const addedNames = (drafts: Array<AssistantDraft>) =>
  drafts.reduce<Array<string>>((names, draft) => {
    const name = draft.name.trim();
    const isNamed = names.some(named => named.localeCompare(name, undefined, { sensitivity: "base" }) === 0);
    return name && !isNamed ? [...names, name] : names;
  }, []);

const DraftHeader: FC<{ draft: AssistantDraft }> = ({ draft }) => (
  <Stack direction="row" sx={{ gap: 2, alignItems: "center", mb: 3 }}>
    <RegistrationControllerIcon2 size="md" />
    <Typography variant="h2" sx={{ lineHeight: 1.4, overflowWrap: "anywhere" }}>
      {draft.name}
    </Typography>
  </Stack>
);

const DraftBody: FC<{ draft: AssistantDraft; sections: Array<DraftSection> }> = ({ draft, sections }) => (
  <>
    <RecordFields draft={draft} />
    {sections.map((section, index) => (
      <Stack key={section.name} sx={{ gap: 0.5, ...growsIn(index + 1) }}>
        {section.name && (
          <Typography sx={{ mt: 4, mb: 2, fontSize: theme => theme.typography.pxToRem(16), fontWeight: 600 }}>
            {section.name}
          </Typography>
        )}
        {section.answers.map(answer => (
          <DraftRow key={answer.questionId} label={answer.label}>
            <AnswerValue answer={answer} />
          </DraftRow>
        ))}
      </Stack>
    ))}
  </>
);

const RecordFields: FC<{ draft: AssistantDraft }> = ({ draft }) => {
  const { t } = useTranslation();
  const { defaultUnitName } = useAssistantApi();
  const units = draft.units ?? [defaultUnitName];

  return (
    <Stack sx={{ gap: ROW_GAP }}>
      <DraftRow label={t("Assistant.Draft.Units")}>
        <Values kind="unit" values={units} />
      </DraftRow>
    </Stack>
  );
};

const DraftClosing: FC<{ additions: Array<string>; isSubmitting: boolean; onSubmit: () => void; order: number }> = ({
  additions,
  isSubmitting,
  onSubmit,
  order,
}) => {
  const { t, formatList } = useTranslation();

  return (
    <Stack sx={{ gap: 2, alignItems: "flex-start", maxWidth: 680, ...growsIn(order) }}>
      {additions.length > 0 && (
        <Typography sx={{ lineHeight: 1.5 }}>
          {t("Assistant.Draft.Additions", { names: formatList(additions) })}
        </Typography>
      )}
      <Typography sx={{ lineHeight: 1.5, fontWeight: 600 }}>{t("Assistant.Draft.Question")}</Typography>
      <Button loading={isSubmitting} onClick={onSubmit}>
        {t("Assistant.Draft.Create")}
      </Button>
    </Stack>
  );
};

const GROW_STEP_MS = 120;

const growsIn = (order: number) =>
  ({
    "animation": `assistantDraftGrow 240ms ease-out ${order * GROW_STEP_MS}ms both`,
    "@keyframes assistantDraftGrow": {
      from: { opacity: 0, transform: "translateY(4px)" },
      to: { opacity: 1, transform: "none" },
    },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  }) as const;

const DraftRow: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <Box sx={ROW_GRID}>
    <Typography sx={{ color: "text.secondary", lineHeight: 1.5, py: 0.75 }}>{label}</Typography>
    <Box sx={{ py: 0.75, minWidth: 0 }}>{children}</Box>
  </Box>
);

const AnswerValue: FC<{ answer: DraftAnswer }> = ({ answer }) => {
  const { t } = useTranslation();

  if (!answer.changed) return <CurrentValue answer={answer} />;

  return (
    <Stack sx={{ gap: 0.5 }}>
      <Stack direction="row" sx={{ gap: 1, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CurrentValue answer={answer} />
        </Box>
        <Chip
          size="small"
          label={t("Assistant.Draft.Changed")}
          sx={{
            flexShrink: 0,
            borderRadius: 0.67,
            fontSize: theme => theme.typography.pxToRem(12),
            fontWeight: 600,
            bgcolor: theme => theme.palette.grey[200],
          }}
        />
      </Stack>
      <Typography
        noWrap
        sx={{ color: "text.secondary", fontSize: theme => theme.typography.pxToRem(12), lineHeight: 1.5 }}
      >
        {t("Assistant.Draft.Earlier", { value: answer.previousValues?.join(", ") ?? answer.previous ?? "" })}
      </Typography>
    </Stack>
  );
};

const CurrentValue: FC<{ answer: DraftAnswer }> = ({ answer }) => {
  const { t } = useTranslation();
  const values = answer.values ?? (answer.value ? [answer.value] : []);
  const valueKeys = uniqueKeys(values);

  if (answer.kind === "yes" || answer.kind === "no") {
    const Icon = answer.kind === "yes" ? YesIcon : CircleXIcon;
    return (
      <Stack direction="row" sx={{ gap: 1, alignItems: "center" }}>
        <Box sx={{ display: "inline-flex", color: answer.kind === "yes" ? "primary.main" : "text.secondary" }}>
          <Icon size={16} />
        </Box>
        <Text>{t(answer.kind === "yes" ? "Assistant.Draft.Yes" : "Assistant.Draft.No")}</Text>
      </Stack>
    );
  }

  if (answer.kind === "options") {
    return values.length === 0 ? (
      <Empty />
    ) : (
      <Stack direction="row" sx={{ gap: 0.5, flexWrap: "wrap" }}>
        {values.map((value, index) => (
          <Chip
            key={valueKeys[index]}
            label={value}
            size="small"
            sx={{
              borderRadius: 0.67,
              bgcolor: theme => theme.palette.grey[300],
              fontSize: theme => theme.typography.pxToRem(12),
              fontWeight: 600,
            }}
          />
        ))}
      </Stack>
    );
  }

  return <Values kind={answer.kind ?? "text"} values={values} />;
};

const Values: FC<{ kind: AssistantDraftValueKind; values: Array<string> }> = ({ kind, values }) => {
  const Glyph = GLYPHS[kind];
  const valueKeys = uniqueKeys(values);

  if (values.length === 0) return <Empty />;

  return (
    <Stack sx={{ gap: ROW_GAP }}>
      {values.map((value, index) => (
        <Stack key={valueKeys[index]} direction="row" sx={{ gap: 1, alignItems: "center" }}>
          {Glyph && (
            <Box sx={{ display: "inline-flex", color: "text.secondary" }}>
              <Glyph size={16} />
            </Box>
          )}
          <Text>{value}</Text>
        </Stack>
      ))}
    </Stack>
  );
};

const Text: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography sx={{ lineHeight: 1.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{children}</Typography>
);

const Empty: FC = () => {
  const { t } = useTranslation();
  return <Typography sx={{ color: "text.disabled", lineHeight: 1.5 }}>{t("Assistant.Draft.Empty")}</Typography>;
};
